export type FAQItem = {
  question: string;
  answer: string;
};

function normalizeWhitespace(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

function stripMarkdownForSchema(text: string): string {
  // Lightweight cleanup for JSON-LD; keeps meaning while avoiding markdown noise.
  let out = text;

  // Links: [text](url) -> text
  out = out.replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1");

  // Inline code/backticks
  out = out.replace(/`{1,3}([^`]+)`{1,3}/g, "$1");

  // Bold/italic markers
  out = out.replace(/\*\*([^*]+)\*\*/g, "$1");
  out = out.replace(/\*([^*]+)\*/g, "$1");
  out = out.replace(/__([^_]+)__/g, "$1");
  out = out.replace(/_([^_]+)_/g, "$1");

  // Blockquotes
  out = out.replace(/^>\s?/gm, "");

  // Headings
  out = out.replace(/^#{1,6}\s+/gm, "");

  return normalizeWhitespace(out);
}

export type ExtractedFaq = {
  faqs: FAQItem[];
  contentWithoutFaq: string;
};

/**
 * Extracts an FAQ section written like:
 *
 * ## Frequently Asked Questions
 * ### Question?
 * Answer...
 *
 * and returns:
 * - `faqs`: parsed Q&A pairs
 * - `contentWithoutFaq`: content with the whole FAQ section removed
 */
export function extractFaqFromMdx(content: string): ExtractedFaq {
  // Match the FAQ H2 section, stopping at the next H2 or EOF.
  const sectionRegex =
    /(^|\n)##\s+Frequently\s+Asked\s+Questions\s*\n([\s\S]*?)(?=\n##\s+|$)/i;
  const match = content.match(sectionRegex);

  if (!match) {
    return { faqs: [], contentWithoutFaq: content };
  }

  const faqBody = match[2] ?? "";
  const faqs: FAQItem[] = [];

  // Extract Q&A pairs (### Question format)
  const questionRegex = /###\s+(.+?)\n\n([\s\S]+?)(?=\n###\s+|$)/g;
  for (const qMatch of faqBody.matchAll(questionRegex)) {
    const question = (qMatch[1] ?? "").trim();
    const answerRaw = (qMatch[2] ?? "").trim();

    if (!question || !answerRaw) continue;

    faqs.push({
      question,
      answer: answerRaw,
    });
  }

  // Remove the entire matched section.
  const startIndex = match.index ?? 0;
  const endIndex = startIndex + match[0].length;
  const contentWithoutFaq =
    `${content.slice(0, startIndex)}\n\n${content.slice(endIndex)}`
      .replace(/\n{3,}/g, "\n\n")
      .trim();

  return { faqs, contentWithoutFaq };
}

export function toFaqSchemaEntities(faqs: FAQItem[]) {
  return faqs
    .filter((f) => Boolean(f.question) && Boolean(f.answer))
    .map((f) => ({
      "@type": "Question",
      name: normalizeWhitespace(f.question),
      acceptedAnswer: {
        "@type": "Answer",
        text: stripMarkdownForSchema(f.answer),
      },
    }));
}

/**
 * FAQ section heading for a post: "FAQ: <subject>". A subtitle after the
 * title's colon is dropped ("On-Page GEO: The Complete Guide…" → "FAQ: On-Page
 * GEO"), unless it is a short qualifier like a month ("FAQ: AI Search News,
 * August 2026").
 */
export function faqHeading(title: string): string {
  const [head, ...rest] = title.split(": ");
  const tail = rest.join(": ");
  if (!tail) return `FAQ: ${head}`;
  return tail.split(" ").length <= 3 ? `FAQ: ${head}, ${tail}` : `FAQ: ${head}`;
}
